import { createFileRoute } from "@tanstack/react-router";
import { PageSkeleton } from "@/components/shared/Skeletons";
import { Download, FileSignature, FileText, Lock } from "lucide-react";
import PageHeader from "@/components/shared/PageHeader";
import StatCard from "@/components/shared/StatCard";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { getFileUrl } from "@/lib/api-client";
import { useStudentCertificates, useStudentProfile, useStudentWorkLogsList } from "@/hooks/useStudentData";

export const Route = createFileRoute("/student/reports/")({ component: StudentReports });

const documentLabels: Record<string, { title: string; desc: string; icon: any }> = {
  resume_url: {
    title: "Curriculum Vitae",
    desc: "The CV uploaded to your student profile.",
    icon: FileText,
  },
  student_id_url: {
    title: "Student ID Document",
    desc: "Your uploaded student identity document.",
    icon: FileSignature,
  },
  dean_letter_url: {
    title: "Dean Letter",
    desc: "Institutional letter submitted for verification.",
    icon: FileSignature,
  },
};

function StudentReports() {
  const { data: profile, isLoading: profileLoading } = useStudentProfile();
  const { data: certificates = [], isLoading: certsLoading } = useStudentCertificates();
  const { data: workLogs = [], isLoading: logsLoading } = useStudentWorkLogsList();
  const isLoading = profileLoading || certsLoading || logsLoading;

  const profileDocs = Object.entries(documentLabels).map(([key, meta]) => ({
    id: key,
    ...meta,
    url: profile?.[key],
    source: "Profile document",
  }));

  const certDocs = (Array.isArray(certificates) ? certificates : []).map((cert: any) => ({
    id: `cert-${cert.id || cert.certificate_id || cert.cert_number}`,
    title: cert.name || cert.title || cert.template_name || "Certificate",
    desc: cert.course || cert.description || "Issued certificate from verified platform records.",
    url: cert.file_url || cert.pdf_url || cert.url,
    source: cert.cert_number || cert.certificate_id || "Certificate",
    icon: FileText,
  }));

  const approvedHours = (Array.isArray(workLogs) ? workLogs : [])
    .filter((log: any) => String(log.status).toLowerCase() === "approved")
    .reduce((sum: number, log: any) => sum + Number(log.hours || 0), 0);

  const docs = [...profileDocs, ...certDocs];

  return (
    <div>
      <PageHeader
        title="Reports and documents"
        subtitle="Real documents and certificates attached to your account. Nothing here is generated from sample data."
        breadcrumbs={[{ label: "Dashboard", path: "/student/dashboard" }, { label: "Reports and Documents" }]}
      />

      {isLoading ? (
        <PageSkeleton />
      ) : (
        <div className="space-y-6">
          <StatCard
            className="sm:max-w-sm"
            label="Approved work hours"
            value={approvedHours}
            hint="Work-hour reports become meaningful as approved logs accumulate."
          />

          <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {docs.map((doc: any) => {
              const Icon = doc.icon;
              const url = doc.url ? getFileUrl(String(doc.url)) : "";
              const isAvailable = Boolean(url);
              return (
                <li
                  key={doc.id}
                  className="flex min-h-56 flex-col rounded-md border border-border bg-card p-4 md:p-5"
                >
                  <span
                    className={cn(
                      "mb-4 flex size-10 items-center justify-center rounded-sm border border-border",
                      isAvailable ? "bg-background text-foreground" : "bg-muted text-muted-foreground",
                    )}
                    aria-hidden
                  >
                    {isAvailable ? <Icon className="size-5" /> : <Lock className="size-5" />}
                  </span>
                  <h3 className="text-[15px] font-medium text-foreground">{doc.title}</h3>
                  <p className="eyebrow mt-1 mb-2 truncate">{doc.source}</p>
                  <p className="flex-1 text-[13px] leading-relaxed text-muted-foreground">{doc.desc}</p>
                  {isAvailable ? (
                    <Button asChild variant="outline" className="mt-4">
                      <a href={url} target="_blank" rel="noreferrer">
                        <Download />
                        Open document
                      </a>
                    </Button>
                  ) : (
                    <Button variant="outline" className="mt-4" disabled>
                      <Lock />
                      Not uploaded
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
